import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@connectrpc/connect-query'
import { useState, useMemo } from 'react'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import { adminGetOrganizationLeaderboard } from '../../gen/cortado/v1/cortado-CortadoService_connectquery'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table'
import { Input } from '../../components/ui/input'
import { Badge } from '../../components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select'
import {
  SortableHeader,
  compareNumbers,
  compareStrings,
  useTableSort,
} from '../../components/ui/sortable-header'
import { Search } from 'lucide-react'

type LeaderboardSortField =
  | 'name'
  | 'health'
  | 'leads'
  | 'conv'
  | 'quotes'
  | 'invoices'
  | 'revenue'
  | 'invoicedPaid'
  | 'emails'
  | 'users'
  | 'subscription'

export const Route = createFileRoute('/_admin/leaderboard')({
  component: LeaderboardPage,
})

const WINDOW_OPTIONS = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
] as const

const COLUMN_COUNT = 12

type BadgeVariant = 'success' | 'warning' | 'orange' | 'danger' | 'info' | 'secondary'

/** Whole days since the given timestamp, or null when it's absent. */
function daysSince(ts?: Timestamp): number | null {
  if (!ts) return null
  return Math.floor((Date.now() - Number(ts.seconds) * 1000) / 86_400_000)
}

/** Health tier from all-time last activity. */
function healthTier(days: number | null): { label: string; variant: BadgeVariant } {
  if (days === null) return { label: 'Never active', variant: 'danger' }
  if (days <= 7) return { label: 'Active', variant: 'success' }
  if (days <= 30) return { label: 'Cooling', variant: 'warning' }
  if (days <= 60) return { label: 'At risk', variant: 'orange' }
  return { label: 'Stale', variant: 'danger' }
}

function isStale(days: number | null): boolean {
  return days === null || days > 60
}

/** Compact relative time, e.g. "3d ago". */
function relativeTime(days: number): string {
  if (days <= 0) return 'today'
  if (days < 30) return `${days}d ago`
  if (days < 365) return `${Math.floor(days / 30)}mo ago`
  return `${Math.floor(days / 365)}y ago`
}

/** Currency-formatted whole dollars from cents; falls back to USD on unknown codes. */
function formatCents(cents: bigint, currency: string): string {
  const dollars = Number(cents) / 100
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 0,
    }).format(dollars)
  } catch {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(dollars)
  }
}

function subscriptionBadge(status: string): { label: string; variant: BadgeVariant } | null {
  switch (status) {
    case 'active':
      return { label: 'Active', variant: 'success' }
    case 'trialing':
      return { label: 'Trialing', variant: 'info' }
    case 'past_due':
      return { label: 'Past due', variant: 'warning' }
    case 'canceled':
      return { label: 'Canceled', variant: 'secondary' }
    case 'revoked':
      return { label: 'Revoked', variant: 'danger' }
    default:
      return null
  }
}

function LeaderboardPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [windowFilter, setWindowFilter] = useState<string>('30')
  const [healthFilter, setHealthFilter] = useState<string>('all')
  const { sortField, sortDirection, toggleSort } = useTableSort<LeaderboardSortField>(
    'revenue',
    'desc',
  )

  const { data, isLoading, isError, error } = useQuery(
    adminGetOrganizationLeaderboard,
    windowFilter === 'all' ? {} : { windowDays: Number(windowFilter) },
  )

  const filteredEntries = useMemo(() => {
    if (!data?.entries) return []
    let result = data.entries

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      result = result.filter(
        (entry) =>
          entry.name.toLowerCase().includes(q) || entry.slug.toLowerCase().includes(q),
      )
    }

    if (healthFilter === 'stale') {
      result = result.filter((entry) => isStale(daysSince(entry.lastActivityAt)))
    }

    const dir = sortDirection === 'asc' ? 1 : -1
    return [...result].sort((a, b) => {
      switch (sortField) {
        case 'name':
          return compareStrings(a.name, b.name) * dir
        case 'health':
          return compareNumbers(
            a.lastActivityAt?.seconds ?? 0n,
            b.lastActivityAt?.seconds ?? 0n,
          ) * dir
        case 'leads':
          return compareNumbers(a.leads, b.leads) * dir
        case 'conv':
          return compareNumbers(
            a.leads > 0 ? a.convertedLeads / a.leads : -1,
            b.leads > 0 ? b.convertedLeads / b.leads : -1,
          ) * dir
        case 'quotes':
          return compareNumbers(a.quotesSent, b.quotesSent) * dir
        case 'invoices':
          return compareNumbers(a.invoicesSent, b.invoicesSent) * dir
        case 'revenue':
          return compareNumbers(a.revenueCollectedCents, b.revenueCollectedCents) * dir
        case 'invoicedPaid':
          return compareNumbers(a.invoicedPaidCents, b.invoicedPaidCents) * dir
        case 'emails':
          return compareNumbers(a.emailsSent, b.emailsSent) * dir
        case 'users':
          return compareNumbers(a.activeUsers, b.activeUsers) * dir
        case 'subscription':
          return compareStrings(a.subscriptionStatus, b.subscriptionStatus) * dir
        default:
          return 0
      }
    })
  }, [data?.entries, searchQuery, healthFilter, sortField, sortDirection])

  if (isError) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Leaderboard</h1>
          <p className="text-muted-foreground text-sm">
            {error instanceof Error ? error.message : 'Unable to load leaderboard'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Leaderboard</h1>
        <p className="text-muted-foreground text-sm">
          Organization performance and engagement
        </p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <Select value={windowFilter} onValueChange={setWindowFilter}>
          <SelectTrigger className="w-full sm:w-[160px]">
            <SelectValue placeholder="Time window" />
          </SelectTrigger>
          <SelectContent>
            {WINDOW_OPTIONS.map((w) => (
              <SelectItem key={w.value} value={w.value}>
                {w.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative w-full sm:max-w-sm">
          <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
          <Input
            placeholder="Search by name or slug..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={healthFilter} onValueChange={setHealthFilter}>
          <SelectTrigger className="w-full sm:w-[160px]">
            <SelectValue placeholder="Filter by health" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All health</SelectItem>
            <SelectItem value="stale">Stale only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">#</TableHead>
              <TableHead>
                <SortableHeader
                  active={sortField === 'name'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('name')}
                >
                  Organization
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader
                  active={sortField === 'health'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('health')}
                >
                  Health
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader
                  active={sortField === 'leads'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('leads')}
                >
                  Leads
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader
                  active={sortField === 'conv'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('conv')}
                >
                  Conv %
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader
                  active={sortField === 'quotes'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('quotes')}
                >
                  Quotes
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader
                  active={sortField === 'invoices'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('invoices')}
                >
                  Invoices sent
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader
                  active={sortField === 'revenue'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('revenue')}
                >
                  Revenue collected
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader
                  active={sortField === 'invoicedPaid'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('invoicedPaid')}
                >
                  Invoiced (paid)
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader
                  active={sortField === 'emails'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('emails')}
                >
                  Emails
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader
                  active={sortField === 'users'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('users')}
                >
                  Users
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader
                  active={sortField === 'subscription'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('subscription')}
                >
                  Subscription
                </SortableHeader>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="text-center text-muted-foreground py-8">
                  Loading leaderboard...
                </TableCell>
              </TableRow>
            ) : !filteredEntries.length ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="text-center text-muted-foreground py-8">
                  {searchQuery.trim() || healthFilter !== 'all'
                    ? 'No organizations match your filters'
                    : 'No organizations found'}
                </TableCell>
              </TableRow>
            ) : (
              filteredEntries.map((entry, index) => {
                const activityDays = daysSince(entry.lastActivityAt)
                const health = healthTier(activityDays)
                const subscription = subscriptionBadge(entry.subscriptionStatus)
                return (
                  <TableRow key={entry.organizationId}>
                    <TableCell className="text-muted-foreground">{index + 1}</TableCell>
                    <TableCell>
                      <Link
                        to="/organizations/$organizationId"
                        params={{ organizationId: entry.organizationId }}
                        className="font-medium hover:underline"
                      >
                        {entry.name}
                      </Link>
                      {entry.slug && (
                        <div className="text-muted-foreground text-xs">{entry.slug}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={health.variant}>{health.label}</Badge>
                      {activityDays !== null && (
                        <div className="text-muted-foreground text-xs mt-1">
                          {relativeTime(activityDays)}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>{entry.leads.toLocaleString()}</TableCell>
                    <TableCell>
                      {entry.leads > 0
                        ? `${Math.round((entry.convertedLeads / entry.leads) * 100)}%`
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <span className="whitespace-nowrap">
                        {entry.quotesSent.toLocaleString()} → {entry.quotesAccepted.toLocaleString()}
                      </span>
                      {entry.quotesSent > 0 && (
                        <span className="text-muted-foreground text-xs ml-1.5">
                          {Math.round((entry.quotesAccepted / entry.quotesSent) * 100)}%
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{entry.invoicesSent.toLocaleString()}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      {formatCents(entry.revenueCollectedCents, entry.defaultCurrency)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {formatCents(entry.invoicedPaidCents, entry.defaultCurrency)}
                    </TableCell>
                    <TableCell>{entry.emailsSent.toLocaleString()}</TableCell>
                    <TableCell>
                      {entry.activeUsers}/{entry.totalUsers}
                    </TableCell>
                    <TableCell>
                      {subscription ? (
                        <Badge variant={subscription.variant}>{subscription.label}</Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
